/**
 * Profitability Breakdown column-preference persistence.
 *
 * Uses Profitability DatamartServices (not OOT) against /v2/table-preferences,
 * always scoped by page_or_component = "ProfitabilityBreakdown" so Networks
 * rows for the same user are never mixed in.
 */
import type { CustomizableColumn } from "Components/Common/ColumnCustomizeDialog";
import DATAMART_SERVICES from "./DatamartServices";
import {
  PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
  dedupeColumnPrefsByField,
  mergeApiColumnsWithDefs,
} from "../Analyse/columnPrefsUtils";

interface ColumnHeader {
  label: string;
  key: string;
  headerTooltip: string;
  pinned: boolean;
  excel: boolean;
  type: string;
  isDisabled: boolean;
  isEditable: boolean;
  displayOnUI: boolean;
}

export type AnalysisColumnPrefsPayload = {
  header: ColumnHeader[];
  columns: CustomizableColumn[];
  exportChecked: boolean;
};

export type AnalysisColumnPrefsLoadResult = {
  columns: CustomizableColumn[];
  exportChecked: boolean;
  ucpId: string;
  pageOrComponent: string;
};

type StoredColumnPreference = {
  id: string;
  field: string;
  headerName: string;
  locked: boolean;
  isVisible: boolean;
  hide: boolean;
  order: number;
};

const buildStoredColumnPreferences = (
  columns: CustomizableColumn[],
  header: AnalysisColumnPrefsPayload["header"],
): StoredColumnPreference[] => {
  const headerMap = new Map(header.map((item) => [item.key, item.label]));
  const deduped = dedupeColumnPrefsByField(columns);

  return deduped.map((column, index) => {
    const field = column.field;
    const locked = !!column.locked;
    const isVisible = locked ? true : column.isVisible === true;
    return {
      id: column.id || field,
      field,
      headerName: headerMap.get(field) || column.headerName || field,
      locked,
      isVisible,
      hide: !isVisible,
      order: column.order ?? index + 1,
    };
  });
};

const parsePreferences = (raw: unknown): { columns?: any[]; exportCheck?: boolean } => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw as { columns?: any[]; exportCheck?: boolean };
};

/**
 * Resolve loaded / empty table-preferences into Redux-ready column prefs.
 * Used by the Analyse bootstrap thunk after header columns are known.
 */
export const resolveAnalysisColumnPrefs = (
  prefsResponse: { data?: any[] } | null | undefined,
  columnDefs: { field: string; headerName: string; locked?: boolean }[],
): AnalysisColumnPrefsLoadResult => {
  const match = prefsResponse?.data?.[0];
  const preferences = parsePreferences(match?.preferences);

  if (preferences?.columns?.length) {
    return {
      columns: mergeApiColumnsWithDefs(preferences.columns, columnDefs),
      exportChecked: !!preferences.exportCheck,
      ucpId: match?.ucp_id != null ? String(match.ucp_id) : "",
      pageOrComponent:
        match?.page_or_component || PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
    };
  }

  return {
    columns: [],
    exportChecked: false,
    ucpId: match?.ucp_id != null ? String(match.ucp_id) : "",
    pageOrComponent: PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
  };
};

/**
 * Load saved preferences for Profitability Breakdown.
 * Returns null when none exist so the caller can fall back to header columns.
 */
export const loadAnalysisColumnPrefs = async (
  columnDefs: { field: string; headerName: string }[],
): Promise<AnalysisColumnPrefsLoadResult | null> => {
  try {
    const response = await DATAMART_SERVICES.getTablePreferences(
      PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
    );
    const resolved = resolveAnalysisColumnPrefs(response, columnDefs);
    if (!resolved.columns.length) {
      return null;
    }
    return resolved;
  } catch (error) {
    console.error("Failed to load analysis column preferences", error);
    return null;
  }
};

/**
 * Persist preferences for Profitability Breakdown.
 * Uses PUT when ucpId exists, otherwise POST.
 */
export const saveAnalysisColumnPrefs = async (
  payload: AnalysisColumnPrefsPayload,
  ucpId?: string,
): Promise<{ ucpId?: string; message?: string } | null> => {
  const storedColumns = buildStoredColumnPreferences(
    payload.columns,
    payload.header,
  );
  const preferences = {
    columns: storedColumns,
    exportCheck: payload.exportChecked,
  };

  try {
    let response = ucpId
      ? await DATAMART_SERVICES.updateTablePreferences(
          PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
          preferences,
          ucpId,
        )
      : await DATAMART_SERVICES.createTablePreferences(
          PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
          preferences,
        );

    // If an existing row cannot be updated (timeout / server error), create a fresh row.
    if (ucpId && !response?.status) {
      response = await DATAMART_SERVICES.createTablePreferences(
        PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
        preferences,
      );
    }

    if (!response?.status) {
      return { message: response?.message || "Failed to save table preferences" };
    }

    return {
      ucpId: response.ucpId || ucpId || "",
    };
  } catch (error) {
    console.error("Failed to save analysis column preferences", error);
    return {
      message:
        error instanceof Error
          ? error.message
          : "Failed to save table preferences",
    };
  }
};

export {
  PROFITABILITY_BREAKDOWN_PAGE_OR_COMPONENT,
  mergeApiColumnsWithDefs,
};
